import { useLocation, useNavigate } from 'react-router';
import { ResourcesTrophyButton } from '../resources';
import { NewsfeedDigestButton } from '../newsfeed';
import { getProbabilityEventIdFromPath } from '../../utils/probability-event.utils';
import type { ProbabilityUser } from '../../types/probability-event.types';
import { useDispatch } from 'react-redux';
import { openAuthModal } from '@/store/slices/auth-modal.slice';
import fullLogo from '../../../../assets/images/logo/full-logo-dark.svg';
import shareSpIcon from '../../../../assets/images/share-sp.svg';
import { useAuthFlow } from '@/hooks';
import {
	useCreateProbabilityShareLinkMutation,
	useGetProbabilityEventDetailsQuery,
} from '../../store/api/probability-event.api';
import { skipToken } from '@reduxjs/toolkit/query';
import { shareLink } from '@/utils';
import { resolveProbabilityShareDescription, ProbabilityShareType } from '@sportspredict/shared';
import {
	PROBABILITY_MIXPANEL_EVENTS,
	trackProbabilityMixpanelEvent,
} from '../../utils/analytics.utils';
import { ArrowLeft } from 'lucide-react';
import { ROUTES } from '@/constants';

interface ProbabilityEventHeaderProps {
	user?: ProbabilityUser;
	eventName?: string;
	eventId?: string;
}

export function ProbabilityEventHeader({ user, eventName, eventId }: ProbabilityEventHeaderProps) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { isWTN } = useAuthFlow();
	const displayEventName = eventName?.trim() || '';

	const activeEventId = eventId || getProbabilityEventIdFromPath(pathname);
	const { data: eventDetails, isLoading: isEventDetailsLoading } =
		useGetProbabilityEventDetailsQuery(activeEventId ? { eventId: activeEventId } : skipToken);
	const partnerLogoUrl =
		eventDetails?.partnerLogoImgVariants?.l ||
		eventDetails?.partnerLogoImgVariants?.m ||
		eventDetails?.partnerLogoImgVariants?.s;

	const [createShareLink] = useCreateProbabilityShareLinkMutation();

	const handleAuthClick = () => {
		dispatch(openAuthModal());
	};

	const handleShare = async () => {
		if (!activeEventId) return;
		trackProbabilityMixpanelEvent(PROBABILITY_MIXPANEL_EVENTS.EVENT_SHARE_CLICKED, {
			eventId: activeEventId,
			eventName: displayEventName,
			share_surface: 'mobile_header',
		});
		const { shortCode } = await createShareLink({
			type: ProbabilityShareType.EVENT,
			data: { eventId: activeEventId },
		}).unwrap();
		const url = `${window.location.origin}/p/${shortCode}`;
		const textParams = { eventName: displayEventName };
		const shareMessage = resolveProbabilityShareDescription(
			ProbabilityShareType.EVENT,
			textParams,
			eventDetails?.shareTexts?.[ProbabilityShareType.EVENT]?.description
		);
		await shareLink({
			url,
			title: shareMessage,
			message: shareMessage,
			skipMobileCheck: true,
		});
	};

	return (
		<header className="fixed left-0 right-0 top-0 z-50 bg-white backdrop-blur-[8px]">
			<div className="border-b-[0.5px] border-[#E1E7F1]">
				<div
					className="flex items-center justify-between px-4 pb-2 pt-1"
					style={{
						height: isWTN ? '84px' : '52px',
						alignItems: isWTN ? 'flex-end' : 'center',
					}}
				>
					<div className="flex items-center gap-2">
						<button
							type="button"
							className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] border-[0.5px] border-[#DCE3F9] bg-white px-[7px] py-0 pl-[6px] shadow-[0px_1px_4px_0px_rgba(0,0,0,0.1)]"
							aria-label="Go back"
							onClick={() => navigate(ROUTES.HOME)}
						>
							<ArrowLeft
								className="h-4 w-4 shrink-0"
								style={{
									width: '16px',
									height: '16px',
									aspectRatio: '1 / 1',
									color: '#5583F6',
								}}
							/>
						</button>
						<div className="flex items-center">
							<img src={fullLogo} alt="Sports Predict" className="h-8 w-[61px]" />
						</div>
					</div>

					<div className="flex items-center gap-2">
						{activeEventId && <NewsfeedDigestButton eventId={activeEventId} />}
						<ResourcesTrophyButton layout="mobile" />

						{user && (
							<button type="button" onClick={handleShare} aria-label="Share event">
								<img
									src={shareSpIcon}
									alt="Share"
									className="h-8 w-8 rounded-lg border border-[#DCE3F9] p-1.5"
								/>
							</button>
						)}

						{user ? null : (
							<button
								type="button"
								onClick={handleAuthClick}
								className="flex h-8 shrink-0 items-center justify-center rounded-lg bg-[linear-gradient(108deg,#1F4A94_0%,#1C2D4A_49.66%,#141821_80.32%)] px-[18px] pb-px text-sm font-medium tracking-[-0.36px] text-white shadow-[0_1px_4px_rgba(12,12,13,0.05)] transition-all hover:scale-[0.98] active:scale-[0.97] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2667FF]"
							>
								Sign In
							</button>
						)}
					</div>
				</div>
			</div>

			{partnerLogoUrl && (
				<div className="border-b-[0.5px] border-[#E5E5E5]">
					<div className="mx-auto flex h-6 items-center justify-center gap-1 py-1 md:max-w-3xl">
						{!isEventDetailsLoading && (
							<>
								<img
									src={partnerLogoUrl}
									alt="Partner"
									className="max-h-4 max-w-8 object-contain"
								/>
								{!partnerLogoUrl && (
									<span className="font-['DM_Sans'] text-[10px] font-medium leading-[13px] tracking-[-0.16px] text-[#848484]">
										Probability Cup by SportsPredict.com
									</span>
								)}
							</>
						)}
					</div>
				</div>
			)}
		</header>
	);
}
